'use client';

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useState } from 'react';

// Interface for comparable properties (matching AvmAgent.tsx)
interface ComparableProperty {
  id: string;
  lat: number;
  lng: number;
  price: number;
  address: string;
  livingArea: number;
  bedrooms: number;
  energyClass: string;
  plotSurface: number;
  buildingConditionScore: number;
  numberOfFrontages: number;
}

// NEW: Interface for the form data
interface TimeframeInfo {
  sellingTimeframe: 'now' | '6m' | '12m' | 'curious' | 'no';
  movePlan?: 'sell_then_buy' | 'bought_then_sell' | 'sell_only' | 'buy_then_sell';
}

interface MapTimeframeFormProps { // Renamed from MapAndContactFormProps
  dictionary: any;
  disableSubmit?: boolean;
  onSubmit: (timeframeInfo: TimeframeInfo) => void; // Updated onSubmit
  comparables?: ComparableProperty[];
  subject_latitude?: number;
  subject_longitude?: number;
  streetName?: string;
  excludedTimeframeOptions?: ('now' | '6m' | '12m' | 'curious' | 'no')[];
  // Prefill for session-resume
  initialSellingTimeframe?: 'now' | '6m' | '12m' | 'curious' | 'no';
  // New: inline follow-up enable and prefill
  enableFollowUp?: boolean;
  initialMovePlan?: 'sell_then_buy' | 'bought_then_sell' | 'sell_only' | 'buy_then_sell';
}

const mapContainerStyle = {
  width: '100%',
  height: '300px' // Adjusted height slightly
};

const defaultCenter = {
  lat: 50.8503, // Default to Brussels if no coords
  lng: 4.3517
};

// TODO: This file should be renamed to MapTimeframeForm.tsx
export default function MapTimeframeForm({
  dictionary,
  onSubmit,
  disableSubmit = false,
  comparables = [], // Default to empty array
  subject_latitude,
  subject_longitude,
  streetName,
  excludedTimeframeOptions = [],
  initialSellingTimeframe,
  enableFollowUp,
  initialMovePlan,
}: MapTimeframeFormProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof TimeframeInfo, string>>>({});
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [sellingTimeframe, setSellingTimeframe] = useState<'now' | '6m' | '12m' | 'curious' | 'no' | ''>('');
  const [showTimeframe, setShowTimeframe] = useState(false);
  // Inline follow-up state
  const [movePlan, setMovePlan] = useState<'' | 'sell_then_buy' | 'bought_then_sell' | 'sell_only' | 'buy_then_sell'>('');
  const [showMovePlan, setShowMovePlan] = useState(false);
  const [timeframeCompleted, setTimeframeCompleted] = useState(false);

  // Prefill timeframe
  useEffect(() => {
    try {
      if (initialSellingTimeframe) {
        setSellingTimeframe(initialSellingTimeframe);
        setShowTimeframe(true);
        // If follow-up is active and timeframe is actionable, show follow-up
        const actionable = ['now','6m','12m','curious'] as const;
        if (enableFollowUp && (actionable as readonly string[]).includes(initialSellingTimeframe)) {
          setShowMovePlan(true);
        }
        setTimeframeCompleted(true);
      }
      if (initialMovePlan) {
        setMovePlan(initialMovePlan);
      }
    } catch (_) {}
  }, [initialSellingTimeframe, initialMovePlan, enableFollowUp]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    mapIds: [process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || ''],
  });

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    setIsMapReady(true);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setIsMapReady(false);
  }, []);

  // Fit map bounds when map is ready and comparables are available
  useEffect(() => {
    if (map && isMapReady && comparables.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      comparables.forEach(property => {
        bounds.extend({ lat: property.lat, lng: property.lng });
      });
      // Include subject property in bounds if coordinates are valid
      if (subject_latitude && subject_longitude) {
        bounds.extend({ lat: subject_latitude, lng: subject_longitude });
      }
      map.fitBounds(bounds, 10);
    }
  }, [map, isMapReady, comparables, subject_latitude, subject_longitude]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const requiresMovePlan = Boolean(enableFollowUp) && ['now','6m','12m','curious'].includes(sellingTimeframe as string);
    if (!sellingTimeframe) {
      setErrors({ sellingTimeframe: dictionary?.sellingForm?.requiredField || 'Please select an option' });
      return;
    }
    if (requiresMovePlan && !movePlan) {
      setErrors({ movePlan: dictionary?.sellingForm?.requiredField || 'Please select an option' });
      return;
    }
    const timeframeInfo: TimeframeInfo = { sellingTimeframe, movePlan: requiresMovePlan ? movePlan as any : undefined };
    setErrors({});
    onSubmit(timeframeInfo);
  };

  // Center map based on subject property or default
  const mapCenter = subject_latitude && subject_longitude
    ? { lat: subject_latitude, lng: subject_longitude }
    : defaultCenter;
    
  const timeframeOptions = [
    { value: 'now', labelKey: 'timeframeNow', default: 'Now' },
    { value: '6m', labelKey: 'timeframe6m', default: 'In 6 months' },
    { value: '12m', labelKey: 'timeframe12m', default: 'In 12 months' },
    { value: 'curious', labelKey: 'timeframeCurious', default: 'Yes, I don\'t know when yet' },
    { value: 'no', labelKey: 'timeframeNo', default: 'No, I don\'t want to sell' },
  ];

  return (
    // Use full width and let parent control bottom margin
    // Wrap in fragment to include style tag
    <>
      <form onSubmit={handleSubmit} className="w-full">
        {/* Map Display Section */}
        <p className="text-base md:text-sm text-zinc-800 mb-2">
          {dictionary?.sellingForm?.mapContactIntro ||
            'Great! Based on the initial details, here are some comparable properties nearby.'}
        </p>
        <div className="rounded-lg google-map-interaction-area h-[300px] mb-4 overflow-hidden border border-zinc-200">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15} // Initial zoom, will be adjusted by fitBounds
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
              }}
            >
              {/* Subject Property Marker */}
              {subject_latitude && subject_longitude && (
                <Marker
                  position={{ lat: subject_latitude, lng: subject_longitude }}
                  title={dictionary?.sellingForm?.subjectPropertyMarkerTitle || 'Your Property'}
                  icon={{
                    url: process.env.NEXT_PUBLIC_HOME_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                    scaledSize: new google.maps.Size(22, 22)
                  }}
                  zIndex={10} // Ensure it's on top
                />
              )}
              {/* Comparable Property Markers */}
              {comparables.map((property) => (
                <Marker
                  key={property.id}
                  position={{ lat: property.lat, lng: property.lng }}
                  icon={{
                    url: process.env.NEXT_PUBLIC_COMPARABLES_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                    scaledSize: new google.maps.Size(18, 18)
                  }}
                />
              ))}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {dictionary?.sellingForm?.loadingMap || 'Loading map...'}
            </div>
          )}
        </div>

        {/* --- Toggle Button --- */}
        {!showTimeframe && (
          <div className="mt-4 text-left">
            <button
              type="button"
              onClick={() => setShowTimeframe(true)}
              className="inline-flex items-center gap-1 bg-white text-zinc-800 hover:text-zinc-950 font-semibold text-base md:text-sm focus:outline-none border border-zinc-800 px-3 py-1 rounded-md hover:bg-zinc-50 transition-colors"
            >
              {dictionary?.sellingForm?.seeMoreToggle || 'Continue'}
              <ChevronDownIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* --- Conditionally Rendered Timeframe Section --- */}
        {showTimeframe && (
          <>
            {/* Selling Timeframe Section */}
            <div className="mt-4">
              <label className="text-base md:text-sm text-zinc-800">
                {(() => {
                  const text =
                    dictionary?.sellingForm?.sellingTimeframeQuestion ||
                    'When do you want to sell your property?';
                  const parts = text.split('[straat]');
                  if (parts.length > 1) {
                    return (
                      <>
                        {parts[0]}
                        <span className="font-semibold">{streetName || ''}</span>
                        {parts[1]}
                      </>
                    );
                  }
                  return text;
                })()}
              </label>
              {/* Timeframe options - always visible, show selected state */}
              <div className="mt-2 flex flex-col gap-2">
                {timeframeOptions
                  .filter(option => !excludedTimeframeOptions.includes(option.value as any))
                  .map(option => (
                    <label
                      key={option.value}
                      htmlFor={option.value}
                      className={`relative flex items-center cursor-pointer p-3 border bg-white rounded-xl transition-colors ${
                        sellingTimeframe === option.value
                          ? 'border-black bg-black border-2'
                          : timeframeCompleted
                            ? 'border-zinc-200 opacity-60'
                            : 'border-zinc-300 hover:bg-zinc-50'
                      }`}
                    >
                      <input
                        type="radio"
                        id={option.value}
                        name="sellingTimeframe"
                        value={option.value}
                        checked={sellingTimeframe === option.value}
                        onChange={(e) => {
                          const val = e.target.value as any;
                          setSellingTimeframe(val);
                          setTimeframeCompleted(true);
                          // Show follow-up inline if applicable
                          const actionable = ['now','6m','12m','curious'];
                          const shouldShow = Boolean(enableFollowUp) && actionable.includes(val);
                          setShowMovePlan(shouldShow);
                          if (!shouldShow) {
                            setMovePlan('');
                          }
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <span className="ml-3 text-base md:text-sm text-zinc-800 flex items-center gap-2">
                        {dictionary?.sellingForm?.[option.labelKey] || option.default}
                        {sellingTimeframe === option.value && timeframeCompleted && (
                          <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </span>
                    </label>
                  ))}
              </div>
              {errors.sellingTimeframe && <p className="text-red-600 text-sm mt-2">{errors.sellingTimeframe}</p>}
            </div>

            {/* Inline Follow-up: Move Plan */}
            {showMovePlan && (
              <>
                {/* Progress separator */}
                <div className="mt-6 mb-4 flex items-center">
                  <div className="flex-1 h-px bg-zinc-200"></div>
                  <div className="px-3 py-1 bg-zinc-100 text-zinc-600 text-xs font-medium rounded-full">
                    {dictionary?.sellingForm?.timeframeFollowUpStep || 'Step 2 of 2'}
                  </div>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>
                <div className="mt-4">
                  <label className="text-base md:text-sm text-zinc-800">
                    {dictionary?.sellingForm?.movePlanQuestion || 'What best describes your plan?'}
                  </label>
                  <div className="mt-2 flex flex-col gap-2">
                    {[
                      { value: 'sell_then_buy', label: dictionary?.sellingForm?.movePlanSellThenBuy || 'I want to sell first and buy after' },
                      { value: 'bought_then_sell', label: dictionary?.sellingForm?.movePlanBoughtThenSell || 'I already bought and want to sell' },
                      { value: 'buy_then_sell', label: dictionary?.sellingForm?.movePlanBuyThenSell || 'I want to buy first and then sell' },
                      { value: 'sell_only', label: dictionary?.sellingForm?.movePlanSellOnly || 'I only want to sell' },
                    ].map(opt => (
                      <label
                        key={opt.value}
                        htmlFor={`mp_${opt.value}`}
                        className="relative flex items-center cursor-pointer p-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2"
                      >
                        <input
                          type="radio"
                          id={`mp_${opt.value}`}
                          name="movePlan"
                          value={opt.value}
                          checked={movePlan === (opt.value as any)}
                          onChange={(e) => setMovePlan(e.target.value as any)}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <span className="ml-3 text-base md:text-sm text-zinc-800">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.movePlan && <p className="text-red-600 text-sm mt-2">{errors.movePlan}</p>}
                </div>
              </>
            )}

            {/* Submit Button */}
            <div className="mt-4">
              <button
                type="submit"
                disabled={disableSubmit || (Boolean(enableFollowUp) && ['now','6m','12m','curious'].includes(sellingTimeframe as string) && !movePlan)}
                className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
              >
                {dictionary?.sellingForm?.continueButton || 'Continue'}
              </button>
            </div>
          </>
        )}
      </form>

      {/* Add missing style block for input container */}
      <style jsx>{`
        .input-container {
          overflow: hidden;
        }
      `}</style>
    </>
  );
} 